// Parent Growth Guide™ — rule-based v1.
// Generates 3–5 personalized monthly recommendations across five pillars:
// Celebrate · Explore · Connect · Support · Grow.

export type StudentSignals = {
  hasCareerDna?: boolean;
  topClusters?: string[];
  topCareers?: string[];
  visionBoardCount?: number;
  goalsCount?: number;
  goalsCompleted?: number;
  lastReflectionMonth?: number | null;
  currentMonthIndex?: number;
  futureSkillsGaps?: string[];
  scholarships?: number;
  universities?: number;
  streak?: number;
};

export type GuidePillar = "celebrate" | "explore" | "connect" | "support" | "grow";

export type ParentRecommendation = {
  id: string;
  pillar: GuidePillar;
  title: string;
  why: string;
};

const PILLAR_META: Record<GuidePillar, { label: string; emoji: string }> = {
  celebrate: { label: "Celebrate", emoji: "🎉" },
  explore:   { label: "Explore",   emoji: "🧭" },
  connect:   { label: "Connect",   emoji: "💬" },
  support:   { label: "Support",   emoji: "🌱" },
  grow:      { label: "Grow",      emoji: "🚀" },
};

export function pillarLabel(p: GuidePillar) {
  return PILLAR_META[p].label;
}
export function pillarEmoji(p: GuidePillar) {
  return PILLAR_META[p].emoji;
}

/** Backwards-compatible alias (older code used `categoryLabel`). */
export const categoryLabel = pillarLabel;

export function generateParentRecommendations(
  s: StudentSignals,
): ParentRecommendation[] {
  const out: ParentRecommendation[] = [];

  // 🎉 Celebrate — always lead with something positive.
  const done = s.goalsCompleted ?? 0;
  if (done > 0) {
    out.push({
      id: "celebrate-goals",
      pillar: "celebrate",
      title: `Celebrate the ${done} goal${done > 1 ? "s" : ""} your child completed this month.`,
      why: "Acknowledging progress builds intrinsic motivation and self-belief.",
    });
  } else if ((s.streak ?? 0) >= 3) {
    out.push({
      id: "celebrate-streak",
      pillar: "celebrate",
      title: `Notice their ${s.streak}-day learning streak — consistency is a superpower.`,
      why: "Recognizing effort matters more than praising outcomes.",
    });
  } else if (s.hasCareerDna) {
    out.push({
      id: "celebrate-dna",
      pillar: "celebrate",
      title: "Celebrate that they've discovered their Career DNA™ this term.",
      why: "Self-awareness is the first step toward purpose.",
    });
  }

  // 🧭 Explore — career discovery.
  if (s.topClusters?.[0]) {
    out.push({
      id: "explore-cluster",
      pillar: "explore",
      title: `Explore two ${s.topClusters[0]} careers together this weekend.`,
      why: "Your child shows strong affinity for this cluster in their Career DNA™.",
    });
  }

  // 💬 Connect — meaningful conversations.
  if (s.topCareers?.[0]) {
    out.push({
      id: "connect-career-talk",
      pillar: "connect",
      title: `Ask why ${s.topCareers[0]} appeared as one of their strongest matches.`,
      why: "Curious questions help your child articulate what excites them.",
    });
  } else if ((s.visionBoardCount ?? 0) > 0) {
    out.push({
      id: "connect-vision",
      pillar: "connect",
      title: "Spend 20 minutes reviewing their Vision Board together.",
      why: "Talking through dreams turns them into direction.",
    });
  } else {
    out.push({
      id: "connect-open",
      pillar: "connect",
      title: "Ask an open question about what they're curious about lately.",
      why: "Curiosity is the seed of purpose.",
    });
  }

  // 🌱 Support — build future-ready skills.
  if (s.futureSkillsGaps?.[0]) {
    out.push({
      id: "support-skill",
      pillar: "support",
      title: `Practise ${s.futureSkillsGaps[0]} through a family activity this week.`,
      why: "FutureReadySkills™ grow fastest through everyday practice at home.",
    });
  } else {
    out.push({
      id: "support-challenge",
      pillar: "support",
      title: "Encourage them to complete one FutureReadySkills™ challenge this month.",
      why: "Small challenges build big capabilities over time.",
    });
  }

  // 🚀 Grow — prepare for the next milestone.
  if (
    s.currentMonthIndex != null &&
    s.lastReflectionMonth !== s.currentMonthIndex
  ) {
    out.push({
      id: "grow-reflection",
      pillar: "grow",
      title: "Remind them to complete this month's reflection before the next milestone.",
      why: "Reflection unlocks the next stage of their Growth Journey.",
    });
  } else if ((s.universities ?? 0) > 0) {
    out.push({
      id: "grow-uni",
      pillar: "grow",
      title: "Explore two recommended universities together.",
      why: "Early exposure to pathways reduces career anxiety later.",
    });
  } else {
    out.push({
      id: "grow-next",
      pillar: "grow",
      title: "Look ahead together — pick one focus area for next month.",
      why: "A shared north star keeps the family moving in the same direction.",
    });
  }

  // Ensure one recommendation per pillar exists where possible; trim to 5.
  const seen = new Set<GuidePillar>();
  const deduped: ParentRecommendation[] = [];
  for (const r of out) {
    if (seen.has(r.pillar)) continue;
    seen.add(r.pillar);
    deduped.push(r);
  }
  // If we ended up with fewer than 3, top up from the raw list.
  for (const r of out) {
    if (deduped.length >= 5) break;
    if (!deduped.find((d) => d.id === r.id)) deduped.push(r);
  }
  return deduped.slice(0, 5);
}
